import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Box, FormControlLabel, Grid, Switch, Typography } from "@mui/material";
import {
    EuroFormat,
    NumberFormat,
    sanitizeNumber,
} from "../../../functions/Formatters";
import Spreadsheet from "react-spreadsheet";
import { toast } from "react-toastify";

const QuantKPIInputSpreadsheet = ({
    field,
    form,
    useSimplified,
    setUseSimplified,
    onDraftChange,
    onBlurSave,
    ...props
}) => {
    const kpi = props.kpi;
    const latestTableRef = useRef(null);

    const segmentNames = useMemo(() => {
        return props.segments?.map((segment) => segment.name);
    }, [props.segments]);

    const layerNames = useMemo(() => {
        if (useSimplified) {
            return ["Totaal"];
        }
        const names =
            props.layers?.map((layer) => layer.layer_details.name) || [];
        return [...names, "Totaal"];
    }, [props.layers, useSimplified]);

    const editableLayerCount = useSimplified ? 1 : props.layers?.length || 0;

    const tableToSegments = useCallback(
        (table) => {
            if (!table) {
                return [];
            }
            return table.map((segment, index) => ({
                segment: props.segments[index].id,
                values: segment
                    .slice(0, editableLayerCount)
                    ?.map((layer, layerIndex) => ({
                        layer: useSimplified
                            ? "total"
                            : props.layers[layerIndex].id,
                        value: layer.value,
                    })),
            }));
        },
        [props.segments, props.layers, editableLayerCount, useSimplified]
    );

    const createData = useCallback(
        (oldData = null) => {
            return props.segments?.map((segment, segmentIndex) => {
                const oldSegment = Array.isArray(oldData)
                    ? oldData[segmentIndex]
                    : null;
                const oldLayers = oldSegment?.values || [];

                let layerValues = props.layers?.map((layer) => ({
                    layer: layer.id,
                    value:
                        oldLayers?.find((l) => l.layer === layer.id)?.value ??
                        null,
                }));

                if (useSimplified) {
                    layerValues = [
                        {
                            layer: "total",
                            value: layerValues.reduce(
                                (sum, layer) =>
                                    sum + (Number(layer.value) || 0),
                                0
                            ),
                        },
                    ];
                }

                return {
                    segment: segment.id,
                    values: layerValues,
                };
            });
        },
        [props.segments, props.layers, useSimplified]
    );

    const segmentData = useMemo(() => {
        if (typeof field.value === "number" || typeof field.value === "string") {
            return createData(field.value);
        }
        if (!(field.value instanceof Array)) {
            return createData(null);
        }
        if (field.value.length !== segmentNames.length) {
            return createData(field.value);
        }
        for (const segment of field.value) {
            if (!(segment instanceof Object) || !("values" in segment)) {
                return createData(null);
            }
            const expectedLayerCount = useSimplified ? 1 : props.layers?.length;
            if (segment.values.length !== expectedLayerCount) {
                return createData(field.value);
            }
        }
        return field.value;
    }, [
        field.value,
        segmentNames.length,
        createData,
        useSimplified,
        props.layers?.length,
    ]);

    const needsNormalization = useMemo(() => {
        return segmentData !== field.value;
    }, [segmentData, field.value]);

    useEffect(() => {
        if (!needsNormalization) {
            return;
        }
        form.setFieldValue(field.name, segmentData);
        onDraftChange?.(segmentData);
    }, [needsNormalization, segmentData, form, field.name, onDraftChange]);

    const mappedData = useMemo(() => {
        if (!segmentData) {
            return null;
        }
        return segmentData.map((segment) => {
            const layerData = segment.values.map((layer) => ({
                ...layer,
                readOnly: props.disabled,
            }));
            const total = segment.values.reduce((sum, layer) => {
                return sum + (Number(layer.value) || 0);
            }, 0);
            if (!useSimplified) {
                layerData.push({
                    value: total,
                    readOnly: true,
                });
            }
            return layerData;
        });
    }, [segmentData, useSimplified, props.disabled]);

    const pushDataToField = useCallback(() => {
        if (!latestTableRef.current) {
            return;
        }
        try {
            const newObj = tableToSegments(latestTableRef.current);
            form.setFieldValue(field.name, newObj);
        } catch (error) {
            console.error("Error pushing data to field:", error);
        }
    }, [tableToSegments, form, field.name]);

    const handleChange = (table) => {
        const sanitized = table?.map((segment, i) =>
            segment?.map((layer, j) => {
                if (!useSimplified && j === segment.length - 1) {
                    return layer;
                }

                let value = sanitizeNumber(layer?.value);

                if (isNaN(Number(value))) {
                    toast.error(
                        `Het getal "${value}" is niet geldig. Controleer de input.`
                    );
                    value = mappedData?.[i]?.[j]?.value;
                }
                return { value: value };
            })
        );

        latestTableRef.current = sanitized;
        onDraftChange?.(tableToSegments(sanitized));
    };

    const valueRenderer = (cell) => {
        let output = sanitizeNumber(cell?.cell?.value);

        if (kpi.unit === "€") {
            output = EuroFormat(output);
        } else {
            output = NumberFormat(output);
        }
        return <Typography textAlign={"right"}>{output}</Typography>;
    };

    const handleSwitchSimplified = (nextSimplified) => {
        setUseSimplified(nextSimplified, createData(field.value));
    };

    return (
        <>
            <Grid item xs={12}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={useSimplified}
                            onChange={() =>
                                handleSwitchSimplified(!useSimplified)
                            }
                        />
                    }
                    label="Vereenvoudigde modus"
                />
                <Box
                    sx={{
                        overflow: "auto",
                        maxWidth: "calc(100vw - 22rem)",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {mappedData && (
                        <Spreadsheet
                            data={mappedData}
                            columnLabels={layerNames}
                            rowLabels={segmentNames}
                            onChange={handleChange}
                            onBlur={() => {
                                pushDataToField();
                                onBlurSave?.();
                            }}
                            allowInsertRow={false}
                            allowAddColumn={false}
                            allowAddRow={false}
                            DataViewer={valueRenderer}
                        />
                    )}
                </Box>
            </Grid>
        </>
    );
};

QuantKPIInputSpreadsheet.displayName =
    "Spreadsheet-style input component for quantitative KPIs";
export default QuantKPIInputSpreadsheet;
